import { useEffect, useRef, useState } from "react";
import { AppLayoutProps } from "@amzn/awsui-components-react/polaris/app-layout";

/** Cloudscape's default, kept on wide screens. */
export const WIDE_NAV_WIDTH = 280;
/** On a laptop: narrower, nav labels are kept short so none wrap. */
export const NARROW_NAV_WIDTH = 220;
/** At or above this viewport width the nav keeps Cloudscape's full width. */
export const WIDE_NAV_MIN_WIDTH = 1600;
/**
 * Below this viewport width the side nav collapses, so tables get the room. A full-width window
 * on a 14" MacBook Pro (1512px) stays above it and keeps the (narrower) nav open.
 */
export const NAV_AUTO_OPEN_MIN_WIDTH = 1280;
const STORAGE_KEY = "lusca.investigations.navOpen";

const viewport = () => window.innerWidth || 0;
const isWide = (viewportWidth = viewport()) =>
  viewportWidth >= NAV_AUTO_OPEN_MIN_WIDTH;

/** Full width on a wide screen, narrower below WIDE_NAV_MIN_WIDTH. */
export const navWidthFor = (viewportWidth: number): number =>
  viewportWidth >= WIDE_NAV_MIN_WIDTH ? WIDE_NAV_WIDTH : NARROW_NAV_WIDTH;

/** The open/closed choice saved on a wide screen (open when never chosen). */
const savedWideOpen = (): boolean => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) !== "false";
  } catch {
    return true;
  }
};

/** Narrow window: collapsed. Wide window: the saved wide-screen choice. */
export const initialNavOpen = (): boolean => isWide() && savedWideOpen();

/**
 * Investigations side-nav state shared by the list and detail pages. Shrinking the window below
 * NAV_AUTO_OPEN_MIN_WIDTH collapses the nav (the user can still open it with ☰); widening it again
 * restores the choice made on a wide screen, which persists per browser.
 */
const useInvestigationsNav = (): Pick<
  AppLayoutProps,
  "navigationWidth" | "navigationOpen" | "onNavigationChange"
> => {
  const [open, setOpen] = useState(initialNavOpen);
  const [navWidth, setNavWidth] = useState(() => navWidthFor(viewport()));
  const wasWide = useRef(isWide());
  useEffect(() => {
    const onResize = () => {
      setNavWidth(navWidthFor(viewport()));
      const nowWide = isWide();
      if (nowWide === wasWide.current) return;
      wasWide.current = nowWide;
      setOpen(nowWide && savedWideOpen());
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return {
    navigationWidth: navWidth,
    navigationOpen: open,
    onNavigationChange: ({ detail }) => {
      setOpen(detail.open);
      // Only a wide-screen choice is remembered; on a narrow window it lasts until the next resize.
      if (!wasWide.current) return;
      try {
        window.localStorage.setItem(STORAGE_KEY, String(detail.open));
      } catch {
        // Storage full or disabled: the choice still applies for this session.
      }
    },
  };
};

export default useInvestigationsNav;
